import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Utensils, Search, X, MapPin, Phone, AlertTriangle } from "lucide-react";
import DailyProductCard from "../components/DailyProductCard";
import { PublicShell, useBrand } from "./PublicHome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

// Prodotti di una sede (/menu-sede/:branchId): stesso shell della Home, back → /home
export default function PublicBranchMenu() {
  const { branchId } = useParams();
  const { brand } = useBrand();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [menuCat, setMenuCat] = useState("__ALL__");
  const [menuSearch, setMenuSearch] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/branch/${branchId}/menu`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [branchId]);

  if (notFound) {
    return (
      <PublicShell title="Prodotti" back="/home" testid="public-branch-menu-notfound">
        <div className="rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-zinc-200 space-y-2">
          <Utensils className="h-10 w-10 mx-auto" style={{ color: brand }} />
          <h2 className="font-heading text-2xl font-bold">Sede non trovata</h2>
          <p className="text-sm text-zinc-500">Verifica il QR code o rivolgiti al personale.</p>
        </div>
      </PublicShell>
    );
  }
  if (!data) return <PublicShell title="Prodotti" back="/home" testid="public-branch-menu-loading"><p className="text-sm text-zinc-500 text-center py-10">Caricamento prodotti…</p></PublicShell>;

  const b = data.branch;
  const allProducts = data.products || [];
  const catCounts = allProducts.reduce((acc, p) => { const c = p.category || "Altro"; acc[c] = (acc[c] || 0) + 1; return acc; }, {});
  const categories = Object.keys(catCounts).sort((a, b) => a.localeCompare(b, "it"));
  const q = menuSearch.trim().toLowerCase();
  const filtered = allProducts.filter((p) => {
    const cat = p.category || "Altro";
    if (menuCat !== "__ALL__" && cat !== menuCat) return false;
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || cat.toLowerCase().includes(q);
  });
  const grouped = filtered.reduce((acc, p) => { const c = p.category || "Altro"; (acc[c] = acc[c] || []).push(p); return acc; }, {});
  const slug = (s) => (s || "altro").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "altro";
  const Chip = ({ active, onClick, testid, children }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`shrink-0 px-3 h-8 rounded-full text-[11px] font-semibold uppercase tracking-wider border transition-colors ${active ? "text-white" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
      style={active ? { background: brand, borderColor: brand } : undefined}>{children}</button>
  );

  return (
    <PublicShell title="Prodotti" back="/home" testid="public-branch-menu-page">
      {/* Intestazione sede */}
      <div className="rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200">
        <div className="relative rounded-2xl overflow-hidden min-h-[120px] flex items-end p-4 text-white" style={{ background: brand }}>
          {b.store_image_url && <><img src={resolveImg(b.store_image_url)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" /></>}
          <div className="relative flex items-center gap-3 min-w-0">
            <div className="h-14 w-14 rounded-2xl overflow-hidden bg-white flex items-center justify-center shrink-0">
              {b.logo_url ? <img src={resolveImg(b.logo_url)} alt="logo" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-full w-full object-cover" /> : <Utensils className="h-6 w-6" style={{ color: brand }} />}
            </div>
            <div className="min-w-0">
              <div className="font-heading text-xl font-extrabold truncate" data-testid="public-branch-menu-name">{b.name}</div>
              {b.address && <div className="text-[11px] opacity-90 flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {b.address}</div>}
              {b.phone && <a href={`tel:${b.phone}`} className="text-[11px] opacity-90 flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" /> {b.phone}</a>}
            </div>
          </div>
        </div>
      </div>

      {data.daily_product && <div className="client-light"><DailyProductCard product={data.daily_product} /></div>}
      {allProducts.length === 0 ? (
        <div className="text-center text-zinc-500 text-sm py-16" data-testid="branch-menu-empty">Nessun prodotto ancora pubblicato per questa sede.</div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <input type="text" value={menuSearch} onChange={(e) => setMenuSearch(e.target.value)} placeholder="Cerca prodotto…" data-testid="branch-menu-search"
              className="w-full h-11 pl-9 pr-9 rounded-full bg-white ring-1 ring-zinc-200 shadow-sm text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2" style={{ "--tw-ring-color": brand }} />
            {menuSearch && <button type="button" onClick={() => setMenuSearch("")} aria-label="Cancella" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"><X className="h-4 w-4" /></button>}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="branch-menu-cats">
            <Chip active={menuCat === "__ALL__"} onClick={() => setMenuCat("__ALL__")} testid="branch-menu-cat-all">Tutte · {allProducts.length}</Chip>
            {categories.map((c) => <Chip key={c} active={menuCat === c} onClick={() => setMenuCat(c)} testid={`branch-menu-cat-${slug(c)}`}>{c} · {catCounts[c]}</Chip>)}
          </div>
          {filtered.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-10">Nessun prodotto corrisponde ai filtri.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="branch-menu-grid">
              {Object.entries(grouped).map(([cat, list]) => (
                <React.Fragment key={cat}>
                  {menuCat === "__ALL__" && !q && <div className="col-span-2 text-[11px] uppercase tracking-widest font-semibold pt-1" style={{ color: brand }}>{cat} <span className="text-zinc-400">· {list.length}</span></div>}
                  {list.map((p) => (
                    <button key={p.id} type="button" onClick={() => setDetail(p)} data-testid={`branch-menu-item-${p.id}`}
                      className="text-left rounded-2xl overflow-hidden bg-white ring-1 ring-zinc-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
                      {p.image_url ? <div className="aspect-square overflow-hidden"><img src={resolveImg(p.image_url)} alt={p.name} loading="lazy" className="w-full h-full object-cover" /></div>
                        : <div className="aspect-square flex items-center justify-center" style={{ background: `${brand}14` }}><Utensils className="h-8 w-8" style={{ color: brand }} /></div>}
                      <div className="p-2.5">
                        <div className="font-heading font-semibold text-sm text-zinc-900 leading-tight line-clamp-2 min-h-[2.5em]">{p.name}</div>
                        {typeof p.price === "number" && p.price > 0 && <div className="mt-1 font-mono text-sm font-semibold" style={{ color: brand }}>€ {p.price.toFixed(2)}</div>}
                      </div>
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </>
      )}
      <div className="text-center pt-2 text-[10px] text-zinc-400">Prodotti aggiornati al {new Date().toLocaleDateString("it-IT")} · {data.app_name}</div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="client-light bg-white border-zinc-200 max-w-md p-0 overflow-hidden" data-testid="branch-menu-detail-dialog">
          {detail && (
            <>
              <DialogHeader className="sr-only"><DialogTitle>{detail.name}</DialogTitle></DialogHeader>
              {detail.image_url ? <div className="aspect-[4/3] overflow-hidden"><img src={resolveImg(detail.image_url)} alt={detail.name} className="w-full h-full object-cover" /></div>
                : <div className="aspect-[4/3] flex items-center justify-center" style={{ background: `${brand}14` }}><Utensils className="h-16 w-16" style={{ color: brand }} /></div>}
              <div className="p-5 space-y-3">
                {detail.category && <span className="pill pill-red text-[10px]">{detail.category}</span>}
                <div>
                  <div className="font-heading text-xl font-bold leading-tight">{detail.name}</div>
                  {typeof detail.price === "number" && detail.price > 0 && <div className="mt-1 font-mono text-lg font-semibold" style={{ color: brand }}>€ {detail.price.toFixed(2)}</div>}
                </div>
                {detail.description && <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{detail.description}</p>}
                {(detail.allergens || []).length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1 mb-1.5"><AlertTriangle className="h-3 w-3" /> Allergeni</div>
                    <div className="flex flex-wrap gap-1.5">{detail.allergens.map((a, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white text-amber-800 border border-amber-200">{a}</span>)}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
